import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { supabase } from '../supabase';
import emailjs from '@emailjs/browser';

const DELIVERY = { casa: 20, hors: 40 };
const H_PAD = 'clamp(40px, 5.5vw, 80px)';

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '' });
  const [zone, setZone] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const fee = zone ? DELIVERY[zone] : null;
  const grandTotal = fee !== null ? total + fee : total;
  const city = zone === 'casa' ? 'Casablanca' : form.city;

  const handleSubmit = async () => {
    if (!form.name || !form.phone) return alert('Veuillez remplir les champs obligatoires.');
    if (!zone) return alert('Veuillez sélectionner votre zone de livraison.');
    if (zone === 'hors' && !form.city.trim()) return alert('Veuillez indiquer votre ville.');
    setLoading(true);
    try {
      for (const item of items) {
        const { data: p } = await supabase.from('products').select('stock_quantity, size_stock').eq('id', item.id).single();
        if (item.size) {
          const sizeQty = p?.size_stock?.[item.size] ?? 0;
          if (sizeQty < item.qty) {
            setLoading(false);
            return alert(`Stock insuffisant pour "${item.name}" taille ${item.size} — seulement ${sizeQty} disponible(s).`);
          }
        } else if (p && p.stock_quantity < item.qty) {
          setLoading(false);
          return alert(`Stock insuffisant pour "${item.name}" — seulement ${p.stock_quantity} disponible(s).`);
        }
        await supabase.from('orders').insert({
          product_id: item.id, customer_name: form.name, customer_email: form.email,
          customer_phone: form.phone, quantity: item.qty, total: item.price * item.qty,
          size: item.size || null, status: 'pending',
        });
        await supabase.rpc('decrement_stock', { p_product_id: item.id, p_qty: item.qty, p_size: item.size || null });
      }
    } catch (e) { /* offline fallback */ }

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          customer_name: form.name, customer_phone: form.phone,
          customer_email: form.email || '—', customer_city: city,
          order_items: items.map(i => `${i.name} × ${i.qty} — Dh ${(i.price * i.qty).toFixed(2)}`).join('\n'),
          delivery_fee: `Dh ${fee}`, order_total: `Dh ${grandTotal.toFixed(2)} MAD`,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
    } catch (e) { /* email failure shouldn't block confirmation */ }

    clearCart();
    setSubmitted(true);
    setLoading(false);
  };

  const pad = isMobile ? '40px 20px' : `clamp(40px, 4vw, 60px) ${H_PAD}`;

  if (submitted) return (
    <main style={{ padding: isMobile ? '60px 20px' : `120px ${H_PAD}`, textAlign: 'center', minHeight: 'calc(100vh - 100px - 280px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? '28px' : '36px', marginBottom: '16px' }}>Merci, {form.name} !</div>
      <p style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#666', marginBottom: '8px' }}>Votre commande a bien été reçue.</p>
      <p style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', letterSpacing: '1px', color: '#999' }}>Nous vous contacterons sous 24h pour confirmer la livraison.</p>
    </main>
  );

  const inputField = (name, label, placeholder, required = false) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'block', fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '8px' }}>
        {label}{required && ' *'}
      </label>
      <input
        name={name} value={form[name]} onChange={handleChange} placeholder={placeholder}
        style={{ width: '100%', padding: '12px 0', border: 'none', borderBottom: '1px solid #ddd', outline: 'none', fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', background: 'transparent', color: '#111', boxSizing: 'border-box' }}
      />
    </div>
  );

  return (
    <main style={{ padding: pad, minHeight: '70vh' }}>
      <h1 style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '18px', letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '48px', fontWeight: 300 }}>
        Finaliser la commande
      </h1>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 360px', gap: isMobile ? '40px' : '60px' }}>
        <div>
          {inputField('name', 'Nom complet', 'Ahmed Benali', true)}
          {inputField('email', 'Email', 'ahmed@email.com')}
          {inputField('phone', 'Téléphone', '+212 6XX XXX XXX', true)}
          {inputField('address', 'Adresse', '12 rue Mohammed V')}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '12px' }}>
              Zone de livraison *
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[['casa', 'Casablanca', '20 MAD'], ['hors', 'Hors Casablanca', '40 MAD']].map(([key, label, price]) => (
                <button key={key} onClick={() => setZone(key)} style={{
                  flex: 1, padding: '14px 12px',
                  border: `1px solid ${zone === key ? '#111' : '#ddd'}`,
                  background: zone === key ? '#111' : '#fff',
                  color: zone === key ? '#fff' : '#555',
                  cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
                  transition: 'all 0.2s', textAlign: 'center',
                }}>
                  <div>{label}</div>
                  <div style={{ marginTop: '4px', opacity: 0.7, fontSize: '9px' }}>{price}</div>
                </button>
              ))}
            </div>
          </div>

          {zone === 'hors' && inputField('city', 'Ville', 'Marrakech, Rabat...', true)}
        </div>

        <div>
          <div style={{ border: '1px solid #eee', padding: isMobile ? '24px' : '32px' }}>
            <h2 style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '20px', fontWeight: 300 }}>Votre commande</h2>
            {items.map(item => (
              <div key={`${item.id}-${item.size}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '15px' }}>{item.name}{item.size ? ` — T.${item.size}` : ''} × {item.qty}</span>
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', color: '#555' }}>Dh {(item.price * item.qty).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
              <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#888' }}>Livraison</span>
              <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', color: fee !== null ? '#111' : '#bbb' }}>{fee !== null ? `Dh ${fee}.00` : '—'}</span>
            </div>
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase' }}>Total</span>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px' }}>Dh {grandTotal.toFixed(2)} MAD</span>
            </div>
            <button onClick={handleSubmit} disabled={loading} style={{
              marginTop: '24px', width: '100%', padding: '16px',
              background: '#111', color: '#fff', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s',
            }}>
              {loading ? 'Envoi...' : 'Confirmer la commande'}
            </button>
            <p style={{ marginTop: '12px', fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', color: '#aaa', letterSpacing: '1px', textAlign: 'center' }}>
              Paiement à la livraison · Livraison 2–4 jours
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
